/**
 * ApiService - Unified API Service Orchestrator
 *
 * Main orchestrator class that provides a unified interface for all API operations
 * in the BiteWise healthcare provider platform. Initializes and manages all
 * domain services, core services, and provides global configuration capabilities.
 *
 * Features:
 * - Unified interface for all domain services
 * - Global token management across all domains
 * - Health check capabilities
 * - Configuration management
 * - Token change subscription system
 * - Singleton pattern for application-wide use
 *
 * @module services/ApiService
 */

import { tokenManager, requestManager } from './shared';
import type { TokenChangeCallback } from './shared';
import {
  userDomain,
  referralDomain,
  dieticianDomain,
  calendlyDomain,
  providerDomain,
  patientDomain,
  approvalDomain,
  mealDomain,
  photoDomain,
  selfReferralDomain,
  shoppingListDomain,
  appointmentDomain,
  patientNotesDomain,
} from './domains';
import type {
  UserDomain,
  ReferralDomain,
  DieticianDomain,
  CalendlyDomain,
  ProviderDomain,
  PatientDomain,
  ApprovalDomain,
  MealDomain,
  PhotoDomain,
  SelfReferralDomain,
  ShoppingListDomain,
  AppointmentDomain,
  PatientNotesDomain,
} from './domains';
import { getAuthService } from './auth/AuthServiceFactory';

/**
 * Configuration interface for ApiService
 */
export interface ApiConfig {
  /** Base URL for API endpoints */
  baseUrl?: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Default headers to include with all requests */
  defaultHeaders?: Record<string, string>;
}

/**
 * Health check response interface
 */
export interface HealthResponse {
  /** Service status indicator */
  status: string;
  /** Timestamp of health check */
  timestamp: string;
  /** Optional version information */
  version?: string;
  /** Optional service details */
  services?: Record<string, string>;
}

/**
 * ApiService - Unified API Service Orchestrator
 *
 * Provides centralized access to all domain services and global API operations.
 * Manages authentication tokens, configuration, and health monitoring across
 * all API domains.
 */
export class ApiService {
  // Domain services
  public readonly user: UserDomain;
  public readonly referral: ReferralDomain;
  public readonly dietician: DieticianDomain;
  public readonly calendly: CalendlyDomain;
  public readonly provider: ProviderDomain;
  public readonly patient: PatientDomain;
  public readonly approval: ApprovalDomain;
  public readonly mealplan: MealDomain;
  public readonly photo: PhotoDomain;
  public readonly selfReferral: SelfReferralDomain;
  public readonly shoppingList: ShoppingListDomain;
  public readonly appointment: AppointmentDomain;
  public readonly patientNotes: PatientNotesDomain;
  public readonly auth: ReturnType<typeof getAuthService>;

  // Future domain services will be added here:
  // public readonly dashboard: DashboardDomain;

  private config: ApiConfig = {};

  /**
   * Initialize ApiService with optional configuration
   *
   * @param config - Optional configuration for the API service
   */
  constructor(config: ApiConfig = {}) {
    // Store configuration
    this.config = { ...config };

    // Initialize domain services
    this.user = userDomain;
    this.referral = referralDomain;
    this.dietician = dieticianDomain;
    this.calendly = calendlyDomain;
    this.provider = providerDomain;
    this.patient = patientDomain;
    this.approval = approvalDomain;
    this.mealplan = mealDomain;
    this.photo = photoDomain;
    this.selfReferral = selfReferralDomain;
    this.shoppingList = shoppingListDomain;
    this.appointment = appointmentDomain;
    this.patientNotes = patientNotesDomain;
    this.auth = getAuthService();

    // Apply global configuration if provided
    if (Object.keys(config).length > 0) {
      this.configure(config);
    }
  }

  /**
   * Set authentication token globally across all domains
   *
   * @param token - Authentication token to set
   */
  setAuthToken(token: string): void {
    tokenManager.setToken(token);
  }

  /**
   * Clear authentication token globally across all domains
   */
  clearAuthToken(): void {
    tokenManager.clearToken();
  }

  /**
   * Get current authentication token
   *
   * @returns Current authentication token or null if not set
   */
  getAuthToken(): string | null {
    return tokenManager.getToken();
  }

  /**
   * Check if user is currently authenticated
   *
   * @returns True if authentication token exists, false otherwise
   */
  isAuthenticated(): boolean {
    return tokenManager.getToken() !== null;
  }

  /**
   * Subscribe to token change events
   *
   * @param callback - Function to call when token changes
   * @returns Unsubscribe function to remove the callback
   */
  onTokenChange(callback: TokenChangeCallback): () => void {
    return tokenManager.onTokenChange(callback);
  }

  /**
   * Perform health check on the API service
   *
   * @returns Promise<HealthResponse> Health status information
   */
  async healthCheck(): Promise<HealthResponse> {
    try {
      // For now, we'll implement a basic health check
      // In the future, this could ping actual health endpoints
      const timestamp = new Date().toISOString();

      // Check if core services are available
      const services: Record<string, string> = {
        tokenManager: tokenManager ? 'available' : 'unavailable',
        requestManager: requestManager ? 'available' : 'unavailable',
        userDomain: this.user ? 'available' : 'unavailable',
        referralDomain: this.referral ? 'available' : 'unavailable',
        dieticianDomain: this.dietician ? 'available' : 'unavailable',
        calendlyDomain: this.calendly ? 'available' : 'unavailable',
        patientNotesDomain: this.patientNotes ? 'available' : 'unavailable',
        providerDomain: this.provider ? 'available' : 'unavailable',
        approvalDomain: this.approval ? 'available' : 'unavailable',
        photoDomain: this.photo ? 'available' : 'unavailable',
        selfReferralDomain: this.selfReferral ? 'available' : 'unavailable',
      };

      // Determine overall status
      const allServicesAvailable = Object.values(services).every(status => status === 'available');
      const status = allServicesAvailable ? 'healthy' : 'degraded';

      return {
        status,
        timestamp,
        version: '1.0.0', // Could be dynamically determined
        services,
      };
    } catch (error) {
      // Return unhealthy status if health check fails
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString(),
        services: {
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  /**
   * Configure the API service with new settings
   *
   * @param config - Configuration options to apply
   */
  configure(config: ApiConfig): void {
    // Merge with existing configuration
    this.config = { ...this.config, ...config };

    // Apply configuration to core services if needed
    // Note: Current architecture doesn't expose configuration methods
    // on TokenManager/RequestManager, but this is where we'd apply them

    // Future implementation might include:
    // if (config.baseUrl) {
    //   // Update base URL for all API clients
    // }
    // if (config.timeout) {
    //   requestManager.setTimeout(config.timeout);
    // }
    // if (config.defaultHeaders) {
    //   // Apply default headers to all API clients
    // }
  }

  /**
   * Get current configuration
   *
   * @returns Current API service configuration
   */
  getConfig(): Readonly<ApiConfig> {
    return { ...this.config };
  }

  /**
   * Reset API service to default state
   * Clears tokens and resets configuration
   */
  reset(): void {
    this.clearAuthToken();
    this.config = {};
  }

  /**
   * Get service information and status
   *
   * @returns Service information object
   */
  getServiceInfo(): {
    version: string;
    domains: string[];
    isAuthenticated: boolean;
    config: Readonly<ApiConfig>;
  } {
    return {
      version: '1.0.0',
      domains: [
        'user',
        'auth',
        'referral',
        'dietician',
        'calendly',
        'provider',
        'approval',
        'mealplan',
        'photo',
        'selfReferral',
        // Future domains will be listed here:
        // 'dashboard',
      ],
      isAuthenticated: this.isAuthenticated(),
      config: this.getConfig(),
    };
  }
}

/**
 * Default ApiService singleton instance
 *
 * This singleton provides application-wide access to all API services
 * and maintains consistent state across the entire application.
 */
export const apiService = new ApiService();
